import transformAccOwnerships from '../../utils/transformAccOwnerships';
import getOptions from '../../utils/getFilterOptions';

import { ACCOUNT_OWNERSHIP_ACCESS } from '../../constants/config';
import { FB, IG } from '../../constants/platformIds';

const compareLabel = (a, b) =>
    a.label && b.label && a.label.localeCompare(b.label);

export const getPlacementOptions = ({
    plcList = {},
    rhList = {},
    hasSeveralRightsholders = false,
}) => {
    const rightsholdersPlacementsOptions = Object.entries(plcList).reduce(
        (acc, [id, pl]) => {
            const [rhId, plId] = id.split('-');

            if (!acc[rhId]) {
                acc[rhId] = {};
            }

            acc[rhId][plId] = {
                value: parseInt(plId),
                label: pl.name,
            };

            return acc;
        },
        [],
    );
    const options = Object.entries(rhList)
        .reduce((acc, [id, rh]) => {
            if (rightsholdersPlacementsOptions[id]) {
                const placements = Object.values(
                    rightsholdersPlacementsOptions[id],
                );
                const hashtagPlacements = placements
                    .filter(p => p.label.endsWith('- Hashtag'))
                    .map(p => p.value);
                const keywordPlacements = placements
                    .filter(
                        p =>
                            p.label.endsWith('- Keyword') ||
                            p.label.endsWith('- Email Text Exposure'),
                    )
                    .map(p => p.value);
                const mentionPlacements = placements
                    .filter(p => p.label.endsWith('- Mention'))
                    .map(p => p.value);
                const verbalMentionPlacements = placements
                    .filter(p => p.label.indexOf('Verbal Mention') !== -1)
                    .map(p => p.value);
                const nonTextPlacements = placements.filter(
                    p =>
                        !p.label.endsWith('- Hashtag') &&
                        !p.label.endsWith('- Keyword') &&
                        !p.label.endsWith('- Mention') &&
                        !(p.label.indexOf('Verbal Mention') !== -1) &&
                        !p.label.endsWith('- Email Text Exposure'),
                );

                acc.push({
                    value: id,
                    label: rh.name,
                    nested: [
                        ...nonTextPlacements.sort(compareLabel),
                        verbalMentionPlacements.length
                            ? {
                                  value: verbalMentionPlacements
                                      .sort()
                                      .join(','),
                                  label: 'Verbal Mention Exposure',
                              }
                            : null,
                        mentionPlacements.length
                            ? {
                                  value: mentionPlacements.sort().join(','),
                                  label: 'Mention Text Exposure',
                              }
                            : null,
                        hashtagPlacements.length
                            ? {
                                  value: hashtagPlacements.sort().join(','),
                                  label: 'Hashtag Text Exposure',
                              }
                            : null,
                        keywordPlacements.length
                            ? {
                                  value: keywordPlacements.sort().join(','),
                                  label: 'Keyword Text Exposure',
                              }
                            : null,
                    ].filter(p => p),
                });
            }
            return acc;
        }, [])
        .sort(compareLabel);
    if (!hasSeveralRightsholders && options.length === 1) {
        const rh = options[0].value;
        return options[0].nested.map(option => {
            option.value = `${rh}-${option.value}`;
            return option;
        });
    }
    return options;
};

export const getAccountOptions = ({ list = {}, userPermissions = [] }) => {
    const filterIds = ACCOUNT_OWNERSHIP_ACCESS.reduce((accum, item) => {
        const { accessId, ownershipId } = item;
        if (!userPermissions || !userPermissions.includes(accessId)) {
            accum.push(ownershipId);
        }
        return accum;
    }, []);

    return Object.values(list)
        .filter(el => !filterIds.includes(el.id))
        .map(el => ({
            value: `${el.id}`,
            label: transformAccOwnerships(el.name),
        }))
        .sort(compareLabel);
};

const platformsPostTypeOptions = {
    [IG.toString()]: [
        {
            value: 1,
            label: 'Instagram Feed',
        },
        {
            value: 2,
            label: 'Instagram Stories',
        },
        {
            value: 5,
            label: 'Instagram Reels',
        },
    ],
    [FB.toString()]: [
        {
            value: 1,
            label: 'Facebook Feed',
        },
        {
            value: 5,
            label: 'Facebook Reels',
        },
    ],
};

export const getPlatformOptions = ({
    list,
    nested = true,
    includeName = false,
    includeBroadcast = true,
}) => {
    const labelAccessor = includeName ? 'name' : 'label';
    let options = Object.entries(list).map(([id, pl]) => {
        const option = {
            value: id,
            [labelAccessor]: pl.name,
        };
        if (nested && platformsPostTypeOptions[id]) {
            return {
                ...option,
                nested: platformsPostTypeOptions[id],
            };
        }
        return option;
    });
    if (!includeBroadcast) {
        options = options.filter(o => o.value != 5);
    }
    return options;
};

export const getRHOptions = ({ rhList, shouldShowLeagueHierarchy }) => {
    if (!shouldShowLeagueHierarchy) return getOptions({ list: rhList });
    const optionsObj = Object.entries(rhList).reduce(
        (acc, [id, { name, leagueId, leagueName }]) => {
            // Don't show rightsholders without placements
            if (acc[leagueId]) {
                acc[leagueId].nested.push({
                    label: name,
                    value: id,
                });
            } else {
                acc[leagueId] = {
                    label: leagueId ? leagueName : 'Other',
                    value: leagueId,
                    nested: [
                        {
                            label: name,
                            value: id,
                        },
                    ],
                };
            }

            return acc;
        },
        {},
    );

    return Object.entries(optionsObj)
        .map(([, { label, value, nested }]) => ({
            label,
            value,
            nested: nested.sort(compareLabel),
        }))
        .sort(compareLabel);
};
